import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Info,
  Target,
} from "lucide-react";
import { BackButton } from "@/components/BackButton";
import { FormField, SelectField } from "@/components/FormField";
import { calcLandedCost, useApp } from "@/context/AppContext";
import {
  analyzeLot,
  rebalanceMix,
  sortingBinsToMix,
  TIER_CONFIDENT_SAMPLE,
  type LotMix,
  type LotTier,
} from "@/features/lotAnalyzer";
import { ManagerOnly } from "@/components/ManagerOnly";

export const Route = createFileRoute("/tools/lot-analyzer")({
  component: () => (
    <ManagerOnly>
      <LotAnalyzerPage />
    </ManagerOnly>
  ),
});

const TIER_DEFS: {
  tier: LotTier;
  key: keyof LotMix;
  description: string;
}[] = [
  {
    tier: "Bulk",
    key: "bulk",
    description: "Common, Uncommon, Trainer, Energy",
  },
  { tier: "Holo", key: "holo", description: "Holo, Reverse Holo, Promo" },
  { tier: "Rare", key: "rare", description: "Rare" },
  { tier: "Hits", key: "hits", description: "Chase / high-value pulls" },
];

const DEFAULT_MIX: LotMix = { bulk: 80, holo: 12, rare: 6, hits: 2 };

const PLATFORM_OPTIONS = [
  "eBay",
  "TCGplayer",
  "Facebook Marketplace",
  "Local",
  "Custom",
];
// NOTE (TNK-48/46): fees → shared Settings "default fees" source; merge this
// with the Bulk Weight Estimator into a single "Lot Estimator" (tabs: Weight /
// From List). docs/settings-audit.md + docs/tools-organization-review.md.
const PLATFORM_FEES: Record<string, number> = {
  eBay: 13.25,
  TCGplayer: 10.25,
  "Facebook Marketplace": 5,
  Local: 0,
  Custom: 0,
};

const MANUAL_OPTION = "__manual__";

function LotAnalyzerPage() {
  const { cards, sales, lots } = useApp();

  const [price, setPrice] = useState("");
  const [cardCount, setCardCount] = useState("");
  const [targetRoi, setTargetRoi] = useState("50");
  const [mix, setMix] = useState<LotMix>(DEFAULT_MIX);
  const [showAbout, setShowAbout] = useState(false);
  const [selectedLotId, setSelectedLotId] = useState<string>(MANUAL_OPTION);
  const [platform, setPlatform] = useState("eBay");
  const [customFee, setCustomFee] = useState("13.25");
  const [paymentFee, setPaymentFee] = useState("2.9");

  const platformFeeRate =
    platform === "Custom"
      ? parseFloat(customFee) || 0
      : (PLATFORM_FEES[platform] ?? 0);
  const paymentFeeRate = parseFloat(paymentFee) || 0;
  const totalFeePct = platformFeeRate + paymentFeeRate;
  const feeRate = totalFeePct / 100;

  const lotOptions = useMemo(
    () => [MANUAL_OPTION, ...lots.map((l) => l.id)],
    [lots],
  );
  const lotOptionLabels = useMemo(
    () => [
      "Manual entry",
      ...lots.map(
        (l) => `${l.seller || "Lot"} — $${calcLandedCost(l).toFixed(2)}`,
      ),
    ],
    [lots],
  );

  const handleLotSelect = (id: string) => {
    setSelectedLotId(id);
    if (id === MANUAL_OPTION) {
      setPrice("");
      setCardCount("");
      setMix(DEFAULT_MIX);
      return;
    }
    const lot = lots.find((l) => l.id === id);
    if (!lot) return;
    setPrice(calcLandedCost(lot).toFixed(2));
    const derived = sortingBinsToMix(lot.sortingBins);
    if (derived) {
      setCardCount(String(derived.includedCount));
      setMix(derived.mix);
    } else {
      const fallback = lot.estimatedCards || 0;
      setCardCount(fallback > 0 ? String(fallback) : "");
      setMix(DEFAULT_MIX);
    }
  };

  const result = useMemo(
    () =>
      analyzeLot({
        price: parseFloat(price) || 0,
        cardCount: parseFloat(cardCount) || 0,
        mix,
        cards,
        sales,
        targetRoi: (parseFloat(targetRoi) || 0) / 100,
        feeRate,
      }),
    [price, cardCount, mix, cards, sales, targetRoi, feeRate],
  );

  const totalMix = mix.bulk + mix.holo + mix.rare + mix.hits;
  const balanced = totalMix === 100;

  const setTier = (tier: LotTier, nextValue: number) => {
    setMix((prev) => rebalanceMix(prev, tier, nextValue));
  };

  const hasInputs =
    (parseFloat(price) || 0) > 0 && (parseFloat(cardCount) || 0) > 0;

  const isLowConfidence =
    result.hasLowConfidenceTiers || result.sampleSize < 10;
  const fmtMoney = (v: number, fuzzy = isLowConfidence) => {
    if (!fuzzy) return `${v < 0 ? "-" : ""}$${Math.abs(v).toFixed(2)}`;
    const abs = Math.abs(v);
    const rounded = abs >= 100 ? Math.round(abs / 5) * 5 : Math.round(abs);
    return `~${v < 0 ? "-" : ""}$${rounded}`;
  };
  const fmtRoi = (v: number) =>
    isLowConfidence ? `~${Math.round(v / 5) * 5}%` : `${v.toFixed(0)}%`;

  const riskClass =
    result.risk === "Low"
      ? "bg-emerald-500/25 text-emerald-400"
      : result.risk === "Medium"
        ? "bg-secondary/30 text-secondary"
        : "bg-rose-500/30 text-rose-400";

  return (
    <section className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 pb-3">
        <BackButton fallback={{ to: "/tools" }} label="Tools" />
      </div>

      <div className="border-b border-border pb-3">
        <h1 className="text-2xl font-bold text-foreground">Lot Analyzer</h1>
        <p className="text-sm text-muted-foreground">
          Estimate profit and a max bid before you commit to a lot purchase.
        </p>
      </div>

      <div
        className="flex-1 space-y-4 overflow-y-auto py-4 pr-1"
        style={{ scrollbarGutter: "stable" }}
      >
        {/* Lot basics */}
        <Section title="Lot Basics">
          {lots.length > 0 && (
            <SelectField
              label="Pull from existing lot"
              value={selectedLotId}
              options={lotOptions}
              optionLabels={lotOptionLabels}
              onSelect={handleLotSelect}
            />
          )}
          <FormField
            label="Asking Price ($)"
            value={price}
            onChangeText={setPrice}
            inputMode="decimal"
            placeholder="e.g. 60.00"
            required
          />
          <FormField
            label="Estimated Card Count"
            value={cardCount}
            onChangeText={setCardCount}
            inputMode="numeric"
            placeholder="e.g. 500"
            required
          />
          <FormField
            label="Target ROI %"
            value={targetRoi}
            onChangeText={setTargetRoi}
            inputMode="decimal"
            placeholder="50"
          />
        </Section>

        {/* Platform */}
        <Section title="Platform">
          <SelectField
            label="Selling Platform"
            value={platform}
            options={PLATFORM_OPTIONS}
            onSelect={setPlatform}
          />
          {platform === "Custom" && (
            <FormField
              label="Platform Fee %"
              value={customFee}
              onChangeText={setCustomFee}
              inputMode="decimal"
              placeholder="13.25"
            />
          )}
          <FormField
            label="Payment Processing Fee %"
            value={paymentFee}
            onChangeText={setPaymentFee}
            inputMode="decimal"
            placeholder="2.9"
          />
          <div className="rounded-md bg-secondary/15 px-3 py-2 text-xs font-medium text-secondary">
            {platform} platform fee: {platformFeeRate.toFixed(2)}% · total fee:{" "}
            {totalFeePct.toFixed(2)}%
          </div>
        </Section>

        {/* Mix sliders */}
        <Section title="Estimated Card Mix">
          <div className="-mt-1 mb-2 flex items-center justify-between">
            <div className="text-xs text-muted-foreground">
              Drag any slider — the others auto-balance to keep total at 100%.
            </div>
            <span
              className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${
                balanced
                  ? "bg-emerald-500/20 text-emerald-500"
                  : "bg-rose-500/20 text-rose-500"
              }`}
            >
              Total {totalMix}%
            </span>
          </div>

          <div className="-mx-1 divide-y divide-border">
            {TIER_DEFS.map(({ tier, key, description }) => {
              const value = mix[key];
              const tierBreakdown = result.breakdown.find(
                (b) => b.tier === tier,
              )!;
              return (
                <div key={tier} className="space-y-1.5 px-1 py-2.5">
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-foreground">
                        {tier}
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        {description}
                      </div>
                    </div>
                    <div className="w-12 text-right text-sm font-bold text-foreground">
                      {value}%
                    </div>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={value}
                    onChange={(e) =>
                      setTier(tier, parseInt(e.target.value, 10))
                    }
                    className="w-full accent-primary"
                  />
                  <div className="text-[10px] text-muted-foreground">
                    {tierBreakdown.cards} cards ·{" "}
                    {tierBreakdown.sampleSize > 0
                      ? `n=${tierBreakdown.sampleSize}`
                      : "no history"}
                  </div>
                </div>
              );
            })}
          </div>
        </Section>

        {/* Outputs */}
        {hasInputs && (
          <>
            {/* Profit card */}
            <div className="space-y-2 rounded-md bg-primary p-4 text-center">
              <div className="text-[11px] font-medium uppercase tracking-wide text-primary-foreground/60">
                {isLowConfidence
                  ? "Rough Net Profit Estimate"
                  : "Expected Net Profit"}
              </div>
              <div
                className={`text-4xl font-bold leading-none ${
                  result.estimatedProfit.expected >= 0
                    ? "text-secondary"
                    : "text-rose-300"
                }`}
              >
                {result.estimatedProfit.expected >= 0 ? "+" : ""}
                {fmtMoney(result.estimatedProfit.expected)}
              </div>
              <div className="text-xs text-primary-foreground/60">
                Range {fmtMoney(result.estimatedProfit.min)} →{" "}
                {fmtMoney(result.estimatedProfit.max)}
              </div>

              {isLowConfidence && (
                <button
                  type="button"
                  onClick={() => setShowAbout(true)}
                  className="mx-auto mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-[11px] font-semibold text-amber-300 hover:bg-white/15"
                >
                  <AlertTriangle className="h-3 w-3" />
                  Low confidence — figures are approximate. Tap for why.
                </button>
              )}

              <div className="mt-3 flex items-stretch divide-x divide-white/15 pt-2">
                <ProfitMeta
                  label="Est Revenue"
                  value={fmtMoney(result.estimatedRevenue.expected)}
                />
                <ProfitMeta label="ROI" value={fmtRoi(result.roi)} />
                <div className="flex flex-1 flex-col items-center gap-1 px-2">
                  <div className="text-[10px] font-medium uppercase tracking-wide text-primary-foreground/60">
                    Risk
                  </div>
                  <span
                    className={`rounded-md px-2 py-0.5 text-xs font-bold ${riskClass}`}
                  >
                    {result.risk}
                  </span>
                </div>
              </div>
            </div>

            {/* Suggested max bid */}
            <div className="space-y-2 rounded-md border border-border bg-card p-4 text-center">
              <div className="inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-foreground">
                <Target className="h-4 w-4 text-primary" />
                {isLowConfidence
                  ? "Suggested Max Bid (rough)"
                  : "Suggested Max Bid"}
              </div>
              <div className="text-3xl font-bold text-primary">
                {fmtMoney(result.suggestedMaxBid)}
              </div>
              <div className="text-[11px] text-muted-foreground">
                Pay no more than this to stay on track for {targetRoi || "0"}%
                ROI at the expected revenue.
              </div>
              {isLowConfidence && (
                <button
                  type="button"
                  onClick={() => setShowAbout(true)}
                  className="mx-auto inline-flex items-center gap-1.5 rounded-md border border-rose-500/40 bg-rose-500/10 px-2.5 py-1 text-[11px] font-semibold text-rose-500 hover:bg-rose-500/15"
                >
                  <AlertTriangle className="h-3 w-3" />
                  Based on limited sales history — treat as a ballpark.
                </button>
              )}
            </div>

            {/* Per-tier breakdown */}
            <Section title="Revenue Breakdown">
              <div className="-mx-1 divide-y divide-border">
                {result.breakdown.map((b) => {
                  const pct =
                    result.estimatedRevenue.expected > 0
                      ? (b.revenue / result.estimatedRevenue.expected) * 100
                      : 0;
                  const tierLowConf = b.lowConfidence;
                  const fmtPerCard = (v: number) => {
                    if (!tierLowConf) return `$${v.toFixed(2)}`;
                    if (v >= 1)
                      return `~$${(Math.round(v * 2) / 2).toFixed(
                        v >= 10 ? 0 : 1,
                      )}`;
                    if (v >= 0.1) return `~$${v.toFixed(1)}`;
                    return `~$${v.toFixed(2)}`;
                  };
                  const fmtBand = () => {
                    if (!tierLowConf)
                      return `$${b.lowPrice.toFixed(2)}–$${b.highPrice.toFixed(2)}`;
                    return `${fmtPerCard(b.lowPrice)}–${fmtPerCard(b.highPrice)}`;
                  };
                  return (
                    <div key={b.tier} className="space-y-1.5 px-1 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 text-sm font-semibold text-foreground">
                          {b.tier}
                        </div>
                        {tierLowConf && b.cards > 0 && (
                          <span className="inline-flex items-center gap-1 rounded bg-rose-500/15 px-1.5 py-0.5 text-[9px] font-semibold text-rose-500">
                            <AlertTriangle className="h-2.5 w-2.5" />
                            {b.noHistory ? "No history" : "Low confidence"}
                          </span>
                        )}
                        <div className="text-sm font-bold text-foreground">
                          {fmtMoney(b.revenue, tierLowConf)}
                        </div>
                      </div>
                      <div className="h-1.5 overflow-hidden rounded-full bg-border">
                        <div
                          className="h-full rounded-full bg-primary"
                          style={{ width: `${Math.min(100, pct)}%` }}
                        />
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        {b.cards} cards →{" "}
                        {b.bundleCount > 0
                          ? `${b.bundleCount} ${b.bundleType.toLowerCase()}`
                          : "no listings"}{" "}
                        × {fmtPerCard(b.avgPrice)}/card
                        {b.sampleSize > 0
                          ? ` (n=${b.sampleSize}, ${fmtBand()})`
                          : " (default — no historical sales)"}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>

            {/* About this estimate */}
            <button
              type="button"
              onClick={() => setShowAbout((v) => !v)}
              className="flex w-full items-center gap-2 rounded-md border border-border bg-card px-3 py-2.5 text-left hover:bg-muted/40"
            >
              <Info className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="flex-1 text-sm font-semibold text-foreground">
                About this estimate
              </span>
              {showAbout ? (
                <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              )}
            </button>
            {showAbout && (
              <div className="space-y-2 rounded-md border border-border bg-card p-3.5 text-xs leading-relaxed text-muted-foreground">
                <p>
                  Per-tier prices are pulled from your own sold-card history
                  (matched by rarity). Tiers with fewer than{" "}
                  {TIER_CONFIDENT_SAMPLE} sales fall back to wider price bands
                  or generic defaults and are flagged as low confidence.
                </p>
                <p>
                  The expected revenue assumes you sell every card. Net revenue
                  applies a {totalFeePct.toFixed(2)}% fee rate ({platform}{" "}
                  {platformFeeRate.toFixed(2)}% + {paymentFeeRate.toFixed(2)}%
                  payment processing). Suggested Max Bid solves for the price at
                  which the expected ROI matches your target.
                </p>
                <p>
                  Total samples: {result.sampleSize}. Risk is High when fewer
                  than {TIER_CONFIDENT_SAMPLE} matching sales exist or any
                  populated tier is low confidence; Medium when total samples
                  are below 30 or the revenue spread is wide; Low otherwise.
                </p>
                <p>
                  This is a one-shot calculator — nothing is saved. Once you
                  actually buy a lot, log it from the Lots tab so future
                  analyses get smarter.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1 rounded-md border border-border bg-card p-4">
      <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      {children}
    </div>
  );
}

function ProfitMeta({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-1 flex-col items-center gap-1 px-2">
      <div className="text-[10px] font-medium uppercase tracking-wide text-primary-foreground/60">
        {label}
      </div>
      <div className="text-base font-bold text-primary-foreground">{value}</div>
    </div>
  );
}
